/**
 * Lead accessibility helpers for frontend forms.
 * Mirrors backend lead accessibility merge logic for booking notes prefill.
 */

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * Get merged accessibility from a lead (address-level with qualification fallback).
 * @param {Object} lead
 * @returns {Object}
 */
export function getLeadAccessibilityFromLead(lead) {
  const pickupAcc = lead?.pickupAddress?.accessibility;
  const deliveryAcc = lead?.deliveryAddress?.accessibility;
  const qualAcc = lead?.qualification?.accessibility || {};

  const hasAddressLevel =
    (pickupAcc && Object.keys(pickupAcc).some((k) => pickupAcc[k] != null)) ||
    (deliveryAcc &&
      Object.keys(deliveryAcc).some((k) => deliveryAcc[k] != null));

  if (hasAddressLevel) {
    return {
      pickupFloors: pickupAcc?.floors ?? qualAcc.pickupFloors ?? undefined,
      pickupStairs: pickupAcc?.stairs ?? qualAcc.pickupStairs ?? undefined,
      dropoffFloors: deliveryAcc?.floors ?? qualAcc.dropoffFloors ?? undefined,
      dropoffStairs: deliveryAcc?.stairs ?? qualAcc.dropoffStairs ?? undefined,
      estateAccess:
        pickupAcc?.estateAccess ??
        deliveryAcc?.estateAccess ??
        qualAcc.estateAccess ??
        undefined,
      estateName:
        pickupAcc?.estateName ??
        deliveryAcc?.estateName ??
        qualAcc.estateName ??
        undefined,
      accessRestrictions:
        pickupAcc?.accessRestrictions ??
        deliveryAcc?.accessRestrictions ??
        qualAcc.accessRestrictions ??
        undefined,
    };
  }

  return qualAcc || {};
}

/**
 * Build booking notes defaults from lead accessibility.
 * @param {Object} lead
 * @param {Object} existingNotes
 * @returns {Object}
 */
export function buildBookingNotesFromLead(lead, existingNotes = {}) {
  const accessibility = getLeadAccessibilityFromLead(lead);
  const notes = { ...existingNotes };

  if (!hasText(notes.stairs)) {
    const stairsParts = [];
    if (accessibility.pickupFloors != null || accessibility.pickupStairs) {
      const pickupParts = [];
      if (accessibility.pickupFloors != null) {
        pickupParts.push(`${accessibility.pickupFloors} floor(s)`);
      }
      if (accessibility.pickupStairs) {
        pickupParts.push("stairs");
      }
      if (pickupParts.length > 0) {
        stairsParts.push(`Pickup: ${pickupParts.join(", ")}`);
      }
    }
    if (accessibility.dropoffFloors != null || accessibility.dropoffStairs) {
      const deliveryParts = [];
      if (accessibility.dropoffFloors != null) {
        deliveryParts.push(`${accessibility.dropoffFloors} floor(s)`);
      }
      if (accessibility.dropoffStairs) {
        deliveryParts.push("stairs");
      }
      if (deliveryParts.length > 0) {
        stairsParts.push(`Delivery: ${deliveryParts.join(", ")}`);
      }
    }
    if (stairsParts.length > 0) {
      notes.stairs = stairsParts.join(". ");
    }
  }

  if (!hasText(notes.estateName) && hasText(accessibility.estateName)) {
    notes.estateName = accessibility.estateName.trim();
  }

  if (!hasText(notes.accessRules) && hasText(accessibility.accessRestrictions)) {
    notes.accessRules = accessibility.accessRestrictions.trim();
  }

  return notes;
}
